import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Heart, ShoppingBag, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useShop } from "../context/ShopContext";
import ProductCard from "../components/ProductCard";

export default function WishlistPage() {
  const { user } = useAuth();
  const { toggleWishlist } = useShop();
  const nav = useNavigate();
  const [items, setItems] = useState(null);

  useEffect(() => {
    if (user === false) return nav("/auth", { state: { from: "/wishlist" } });
    if (user) api.get("/wishlist").then(({ data }) => setItems(data.items)).catch(() => setItems([]));
  }, [user, nav]);

  if (!user || items === null) {
    return <div className="max-w-7xl mx-auto px-4 py-20 text-center text-stone-400 text-sm">Loading your wishlist…</div>;
  }

  if (!items.length) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-24 text-center" data-testid="empty-wishlist">
        <div className="w-20 h-20 mx-auto rounded-3xl bg-[#F5EFE6] grid place-items-center"><Heart className="text-[#B45309]" size={34} /></div>
        <h1 className="font-serif text-3xl font-bold text-stone-900 mt-6">Save gifts you love and find them later</h1>
        <p className="text-stone-500 mt-3">Tap the heart on any gift and it will wait for you right here.</p>
        <Link to="/shop" className="inline-flex items-center gap-2 mt-8 px-8 py-3.5 rounded-full bg-[#B45309] text-white text-sm font-bold hover:bg-[#92400E] transition-colors" data-testid="empty-wishlist-discover">
          Discover Gifts <ArrowRight size={15} />
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-10 lg:py-14" data-testid="wishlist-page">
      <h1 className="font-serif text-3xl sm:text-4xl font-bold text-stone-900 tracking-tight">Your Wishlist</h1>
      <p className="text-stone-500 mt-1">{items.length} gift{items.length > 1 ? "s" : ""} you adore.</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-10">
        {items.map((p) => (
          <div key={p.id} className="relative">
            <ProductCard product={p} />
            <button
              onClick={async () => {
                await toggleWishlist(p.id);
                setItems((prev) => prev.filter((x) => x.id !== p.id));
                toast.success("Removed from wishlist");
              }}
              className="absolute top-3 right-3 z-10 px-3 h-8 rounded-full bg-white/95 text-xs font-bold text-stone-600 shadow hover:text-red-600 transition-colors"
              data-testid={`wishlist-remove-${p.id}`}
            >
              Remove
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
